// DropdownControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Control } from '../ControlRenderer';
import { evaluateTemplate, get, set } from '../utils/Utils';
import { Select, MenuItem, FormControl, Typography, TextField, Checkbox, ListItemText, Autocomplete, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType, useFormData } from '../../../components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import { useAuth } from '@/components/AuthProvider';
import useActionHandler from '@/hooks/useActionHandler';
import ModalBox, { Action } from '../ModalBox';
import ControlIcons from '../ControlIcons';

const DEFAULT_WRAP_CHARS = '/|-';

const renderWithWrapChars = (value: any, wrapChars: string): React.ReactNode => {
  const text = value === null || value === undefined ? '' : String(value);
  if (!text || !wrapChars) {
    return text;
  }

  const breakChars = new Set(Array.from(wrapChars));
  const parts: React.ReactNode[] = [];
  const chars = Array.from(text);

  chars.forEach((char, index) => {
    parts.push(char);
    if (breakChars.has(char) && index < chars.length - 1) {
      parts.push(<wbr key={`wrap-${index}`} />);
    }
  });

  return parts;
};

/**
 * Represents a dropdown control with properties to manage its source items,
 * selection mode, and autocomplete feature. Extends the base Control interface.
 */
export interface DropdownControl extends Control {
  /**
   * A string used as a key to identify the list of items associated with this dropdown.
   * It helps in rendering or fetching the dropdown's data source.
   */
  itemsKey: string;

  /**
   * Represents the actual items that populate the dropdown.
   * This can be any enumerable set that the dropdown will iterate over to display available options.
   */
  items: any;

  /**
   * Allows support for multi-selection within the dropdown.
   * If true, users can select multiple items simultaneously. (Optional)
   */
  multiple?: boolean;

  /**
   * Switches the dropdown to autocomplete mode, enabling users to search and select
   * items based on partial input matches. This enhances usability for dropdowns with large datasets. (Optional)
   */
  autocomplete?: boolean;

  /** Allows the user to enter a custom value that is not in the list */
  freeSolo?: boolean;

  /**
   * Allows to set null value in the dropdown list
   * If true, an empty item is added to the dropdown list to be able to select null value. (Optional)
   */
  nullable?: boolean;

  /**
   * Allows to specify a placeholder string in the dropdown control (Optional)
   */
  placeholder?: string;

  /**
   * Allows to display codes along with the values (Optional)
   */
  displayCodes?: boolean;

  /**
   * Action to be executed when the value of the dropdown changes (Optional)
   */
  changeAction?: Action;
  /**
   * If true, the first item in the dropdown will be automatically selected
   * when the control is initialized. (Optional)
   */
  autoSelectFirst?: boolean;

  /** Whether to hide the tooltip for the displayed value. */
  hideTooltip?: boolean;

  /**
   * Characters where dropdown option text may wrap if it does not fit.
   * Defaults to "/|-" when not specified.
   */
  wrapChars?: string;
}

interface DropdownControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

const DropdownControl: React.FC<DropdownControlProps> = ({ control, data, onDataChange, mode, onComplete, onError, onClose }) => {
  const { executeAction, UIAction, handleSuccess, handleFailure, handleClose } = useActionHandler({ onComplete: onComplete, onError: onError, onClose: onClose });
  const { errors, warnings, rule, validateData, setDataContext } = useValidation();
  const theme = useTheme();
  const { token } = useAuth();
  const handleFieldChange = (fieldName: string, value: string | string[] | null) => {
    // Handle Select All/Deselect All for multiple select
    if (multiple && Array.isArray(value)) {
      const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
      const selectAllIndex = value.indexOf('__SELECT_ALL__');

      if (selectAllIndex !== -1) {
        // Select All was clicked
        const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
        const allActualValues = actualOptions.map((opt: any[]) => opt[0]);

        // If all items are selected, deselect all; otherwise select all
        if (currentSelections.length === allActualValues.length) {
          value = []; // Deselect all
        } else {
          value = allActualValues; // Select all actual options
        }
      } else {
        // Regular selection, filter out Select All if it was previously selected
        value = value.filter((val: any) => val !== '__SELECT_ALL__');
      }
    }

    setSelectedValue(value);
    if (value === '') {
      set(data, fieldName, null);
      onDataChange(null);
    }
    else {
      set(data, fieldName, value);
      onDataChange(data);
    }
    validateData();

    const dropdown = control as DropdownControl;
    if (dropdown.changeAction) {
      executeAction(dropdown.changeAction, data);
    }
  };

  const { formData, prepareLookupData } = useFormData();
  const multiple = (control as DropdownControl).multiple ?? false;
  const autocomplete = (control as DropdownControl).autocomplete ?? false;
  const freeSolo = (control as DropdownControl).freeSolo ?? false;

  const [selectedValue, setSelectedValue] = useState<any>(multiple ? [] : '');
  const [inputValue, setInputValue] = useState('');

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;
  const warningMessage = warnings && control.dataKey ? warnings[control.dataKey] : undefined;

  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage || !!warningMessage;

  const dropdownControl = control as DropdownControl;

  useEffect(() => {
    const getInitialSelectedValue = () => {
      let currentValue = get(data, control.dataKey ?? '');
      if (currentValue === undefined) currentValue = null;
      return multiple
        ? (Array.isArray(currentValue) ? currentValue : [])
        : (Array.isArray(currentValue) ? '' : (currentValue ?? ''));
    };

    const initial = getInitialSelectedValue();
    setSelectedValue(initial);
    if (!multiple && typeof initial === 'string') {
      setInputValue(initial);
    }
  }, [control.dataKey, data, multiple, onDataChange]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  const itemsKey = useMemo(() => {
    if (dropdownControl.itemsKey) {
      return evaluateTemplate(dropdownControl.itemsKey, data, formData);
    }
    return null;
  }, [dropdownControl.itemsKey, data, formData]);

  useEffect(() => {
    // we need to wait for token before retrieving the lookups
    if (itemsKey && token) {
      prepareLookupData(itemsKey, "", undefined);
    }
  }, [token, itemsKey, prepareLookupData]);

  let options = dropdownControl.items;
  let optionsFromFormData = get(formData, itemsKey ?? '', null);
  if (Array.isArray(optionsFromFormData) && optionsFromFormData.length > 2) {
    // Sort options alphabetically case-insensitive based on display label
    optionsFromFormData = optionsFromFormData.slice().sort((a: any, b: any) => a[1].toString().localeCompare(b[1].toString(), undefined, { sensitivity: 'base' }));
  }
  if (Array.isArray(options)) {
    if (optionsFromFormData) {
      options = options.concat(optionsFromFormData);
    }
  } else if (optionsFromFormData) {
    options = optionsFromFormData;
  } else {
    options = [];
  }
  let currentMode = control.mode ? control.mode : mode;
  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;
  const minWidthStyle = control.minWidth ? { minWidth: control.minWidth } : { minWidth: 0 };
  const hideTooltip = dropdownControl.hideTooltip ?? false;
  const wrapChars = dropdownControl.wrapChars ?? DEFAULT_WRAP_CHARS;
  const getTooltipTitle = (value: any) => value !== null && value !== undefined && value !== '' && !hideTooltip ? value.toString() : '';
  const tooltipPopperProps = {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, -10], // move closer to the item
        },
      },
    ],
  };

  if (typeof options === 'string') {
    // in case of plain text this is an info message
    return (
      <Tooltip title={getTooltipTitle(options)} PopperProps={tooltipPopperProps} arrow>
        <TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          defaultValue={options}
          inputProps={{ readOnly: true }}
          style={minWidthStyle}
        />
      </Tooltip>
    )
  }

  // Add a nullable option if specified (but not for multiple select)
  if (dropdownControl.nullable && !multiple) {
    options = [['', '']].concat(options); // Empty selection is stored as null by handleFieldChange.
  }

  // Sort options alphabetically case-insensitive based on display label
  const sortedOptions = options.slice().sort((a: any, b: any) => a[1].toString().localeCompare(b[1].toString(), undefined, { sensitivity: 'base' }));

  // For multiple select, add "Select All" option at the top
  if (multiple) {
    const selectAllOption = ['__SELECT_ALL__', 'Select All'];
    options = [selectAllOption].concat(sortedOptions);
  } else {
    options = sortedOptions;
  }

  // Function to get value by key
  const getValueByKey = (key: any) => {
    //console.log('DropdownControl.getValueByKey');
    //console.log(options);
    //console.log(key);
    if (key === null || key === '') return ''; // Return an empty string for null
    for (let i = 0; i < options.length; i++) {
      if (options[i][0] === key) {
        return options[i][1]; // Return the value corresponding to the key
      }
    }
    return null; // Return null if key is not found
  };

  // Function to get display value for multiple selection
  const getDisplayValues = (keys: string[]) => {
    return keys.map((key) => getValueByKey(key)).join(', ');
  };

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue)}
      </Typography>
    )
  }

  const getAutocompleteOptionLabel = (option: any) => {
    const label = Array.isArray(option) ? option[1] : option;
    return label === null || label === undefined ? '' : String(label);
  };

  const getAutocompleteValue = () => {
    if (multiple) {
      return options.filter(
        (option: any[]) =>
          selectedValue?.includes(option[0]) && option[0] !== '__SELECT_ALL__'
      );
    }

    const selectedOption = options.find((option: any[]) => option[0] === selectedValue);
    if (selectedOption) {
      return selectedOption;
    }

    return freeSolo ? (selectedValue ?? '') : null;
  };
  const commonAutocompleteProps = {
    multiple,
    className: `eform-dropdown ${className}`,
    clearOnEscape: true,
    options, // [[key, label], ...]
    getOptionLabel: getAutocompleteOptionLabel,
    isOptionEqualToValue: (option: any, value: any) => {
      if (Array.isArray(option) && Array.isArray(value)) {
        return option[0] === value[0];
      }
      return option === value;
    },
    value: getAutocompleteValue(),
    renderInput: (params: any) => (
      <TextField
        {...params}
        placeholder={dropdownControl.placeholder}
        label={control.label}
        variant="standard"
        style={minWidthStyle}
      />
    ),
    renderOption: (props: any, option: any) => (
      <li {...props}>
        {renderWithWrapChars(getAutocompleteOptionLabel(option), wrapChars)}
      </li>
    ),
    style: minWidthStyle,
  };

  // When freeSolo = false → use original behavior (no inputValue/onInputChange)
  const autocompleteProps = !freeSolo
    ? {
      ...commonAutocompleteProps,
      onChange: (event: any, newValue: any) =>
        handleFieldChange(
          control.dataKey ?? '',
          multiple
            ? (newValue as any[]).map((item) => item[0])
            : newValue
              ? newValue[0]
              : ''
        )
    }
    : {
      // freeSolo = true → extend with controlled input + string support
      ...commonAutocompleteProps,
      freeSolo,
      inputValue,
      onChange: (event: any, newValue: any) => {
        if (multiple) {
          const values = (newValue as any[]).map((item) =>
            Array.isArray(item) ? item[0] : item
          );
          handleFieldChange(control.dataKey ?? '', values);
        } else {
          let value: any = null;
          if (Array.isArray(newValue)) value = newValue[0];
          else if (typeof newValue === 'string') value = newValue; // arbitrary string
          else if (newValue) value = (newValue as any)[0];
          else value = '';
          handleFieldChange(control.dataKey ?? '', value);
        }
      },
      onInputChange: (event: any, newInputValue: string) => {
        setInputValue(newInputValue);
        // keep data in sync while typing for single-select freeSolo
        if (!multiple) {
          handleFieldChange(control.dataKey ?? '', newInputValue);
        }
      }
    };

  const displayValue = multiple && Array.isArray(selectedValue) ? getDisplayValues(selectedValue) : getValueByKey(selectedValue) ?? '';

  return (
    <div key={control.name} style={{ ...minWidthStyle }}>
      {currentMode !== 'view' ? (
        options && autocomplete ? (
          hasControlIcons ? (
            <FormControl variant="standard" className="eform-dropdown" error={!!errorMessage}>
              <Autocomplete {...autocompleteProps} />
              <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} offsetRight={15} />
            </FormControl>
          ) : (
            <Autocomplete {...autocompleteProps} />
          )
        ) : (
          errorMessage ? (
            <FormControl variant="standard" className="eform-dropdown" error={!!errorMessage}><Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              error={!!errorMessage}
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => {
                const isSelectAll = option[0] === '__SELECT_ALL__';
                const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
                const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
                const allSelected = currentSelections.length === actualOptions.length && actualOptions.length > 0;

                return (
                  <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                    {multiple && <Checkbox checked={isSelectAll ? allSelected : (Array.isArray(selectedValue) && selectedValue.includes(option[0]))} />}
                    <ListItemText primary={renderWithWrapChars(option[1], wrapChars)} />
                  </MenuItem>
                );
              })}
            </Select>
              <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} offsetRight={15} />
            </FormControl>
          ) : (
            <Select
              multiple={multiple}
              className={`eform-dropdown ${className}`}
              MenuProps={{ disableScrollLock: true }}
              label={control.label}
              name={control.name}
              value={selectedValue}
              onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value as string | string[])}
              renderValue={(selected) => multiple && Array.isArray(selected) ? getDisplayValues(selected) : getValueByKey(selected)}
              style={minWidthStyle}
            >
              {options && options.map((option: Array<any>, index: React.Key | null | undefined) => {
                const isSelectAll = option[0] === '__SELECT_ALL__';
                const actualOptions = options.filter((opt: any[]) => opt[0] !== '__SELECT_ALL__');
                const currentSelections = Array.isArray(selectedValue) ? selectedValue.filter((val: any) => val !== '__SELECT_ALL__') : [];
                const allSelected = currentSelections.length === actualOptions.length && actualOptions.length > 0;

                return (
                  <MenuItem key={index} value={option[0]} className="eform-dropdown-menuitem">
                    {multiple && <Checkbox checked={isSelectAll ? allSelected : (Array.isArray(selectedValue) && selectedValue.includes(option[0]))} />}
                    <ListItemText primary={renderWithWrapChars(option[1], wrapChars)} />
                  </MenuItem>
                );
              })}
            </Select>
          ))
      ) : (
        <Tooltip title={getTooltipTitle(displayValue)} PopperProps={tooltipPopperProps} arrow>
          <TextField
            variant="standard"
            type="text"
            className={className}
            fullWidth
            value={displayValue}
            inputProps={{ readOnly: true }}
            style={minWidthStyle}
          />
        </Tooltip>
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction, data)}
          onError={() => handleFailure(UIAction, data)}
          onClose={() => handleClose(UIAction, data)} />}
    </div>
  );
};

export default DropdownControl;
